// Column Metadata Editor Component
// UI for editing column metadata (validation, formatting, display, conditions)

import React, { useState } from 'react';
import type { ColumnMetadata, FieldType } from '../../types/columnMetadata';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../ui/accordion';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';

type ColumnMetadataEditorProps = {
  fieldType: FieldType;
  metadata?: ColumnMetadata;
  onChange: (metadata: ColumnMetadata) => void;
};

export const ColumnMetadataEditor: React.FC<ColumnMetadataEditorProps> = ({
  fieldType,
  metadata = {},
  onChange,
}) => {
  const [localMetadata, setLocalMetadata] = useState<ColumnMetadata>(metadata);

  const updateMetadata = (updates: Partial<ColumnMetadata>) => {
    const newMetadata = { ...localMetadata, ...updates };
    setLocalMetadata(newMetadata);
    onChange(newMetadata);
  };

  const updateValidation = (updates: Partial<ColumnMetadata['validation']>) => {
    updateMetadata({
      validation: { ...localMetadata.validation, ...updates },
    });
  };

  const updateFormatting = (updates: Partial<ColumnMetadata['formatting']>) => {
    updateMetadata({
      formatting: { ...localMetadata.formatting, ...updates },
    });
  };

  const updateDisplay = (updates: Partial<ColumnMetadata['display']>) => {
    updateMetadata({
      display: { ...localMetadata.display, ...updates },
    });
  };

  const updateUI = (updates: Partial<ColumnMetadata['ui']>) => {
    updateMetadata({
      ui: { ...localMetadata.ui, ...updates },
    });
  };

  const isTextType = ['singleLineText', 'longText', 'email', 'url', 'phoneNumber'].includes(fieldType);
  const isDateType = fieldType === 'date';
  const isNumberType = fieldType === 'number';
  const isDescriptionType = fieldType === 'longText';

  return (
    <div className="space-y-4">
      <Tabs defaultValue="validation" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="validation">Validation</TabsTrigger>
          <TabsTrigger value="formatting">Formatting</TabsTrigger>
          <TabsTrigger value="display">Display</TabsTrigger>
          <TabsTrigger value="ui">UI</TabsTrigger>
        </TabsList>

        {/* Validation Tab */}
        <TabsContent value="validation" className="space-y-4">
          <Accordion type="single" collapsible className="w-full">
            {/* Required */}
            <AccordionItem value="required">
              <AccordionTrigger>Required</AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Field is required</Label>
                  <Switch
                    checked={typeof localMetadata.validation?.required === 'boolean' 
                      ? localMetadata.validation.required 
                      : !!localMetadata.validation?.required}
                    onCheckedChange={(checked) => {
                      updateValidation({ required: checked });
                    }}
                  />
                </div>
                {typeof localMetadata.validation?.required === 'object' && (
                  <div className="space-y-2">
                    <Label>Condition (optional)</Label>
                    <Input
                      placeholder="e.g., status === 'active'"
                      value={localMetadata.validation.required.condition || ''}
                      onChange={(e) =>
                        updateValidation({
                          required: {
                            ...localMetadata.validation!.required as { condition?: string; message?: string },
                            condition: e.target.value,
                          },
                        })
                      }
                    />
                    <Label>Custom error message</Label>
                    <Input
                      placeholder="e.g., This field is required when status is active"
                      value={(localMetadata.validation.required as { message?: string }).message || ''}
                      onChange={(e) =>
                        updateValidation({
                          required: {
                            ...localMetadata.validation!.required as { condition?: string; message?: string },
                            message: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Text Validation */}
            {isTextType && (
              <AccordionItem value="text-validation">
                <AccordionTrigger>Text Validation</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Length</Label>
                      <Input
                        type="number"
                        value={localMetadata.validation?.text?.minLength || ''}
                        onChange={(e) =>
                          updateValidation({
                            text: {
                              ...localMetadata.validation?.text,
                              minLength: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Max Length</Label>
                      <Input
                        type="number"
                        value={localMetadata.validation?.text?.maxLength || ''}
                        onChange={(e) =>
                          updateValidation({
                            text: {
                              ...localMetadata.validation?.text,
                              maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Pattern (Regex)</Label>
                    <Input
                      placeholder="e.g., ^[A-Z]+$"
                      value={localMetadata.validation?.text?.pattern || ''}
                      onChange={(e) =>
                        updateValidation({
                          text: {
                            ...localMetadata.validation?.text,
                            pattern: e.target.value || undefined,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Auto-trim whitespace</Label>
                      <Switch
                        checked={localMetadata.validation?.text?.trim || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            text: {
                              ...localMetadata.validation?.text,
                              trim: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>No whitespace allowed</Label>
                      <Switch
                        checked={localMetadata.validation?.text?.noWhitespace || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            text: {
                              ...localMetadata.validation?.text,
                              noWhitespace: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>No special characters</Label>
                      <Switch
                        checked={localMetadata.validation?.text?.noSpecialChars || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            text: {
                              ...localMetadata.validation?.text,
                              noSpecialChars: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Date Validation */}
            {isDateType && (
              <AccordionItem value="date-validation">
                <AccordionTrigger>Date Validation</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Date</Label>
                      <Input
                        placeholder="e.g., today, -30d, 2024-01-01"
                        value={localMetadata.validation?.date?.minDate || ''}
                        onChange={(e) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              minDate: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Max Date</Label>
                      <Input
                        placeholder="e.g., +365d, 2025-12-31"
                        value={localMetadata.validation?.date?.maxDate || ''}
                        onChange={(e) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              maxDate: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Age (years)</Label>
                      <Input
                        type="number"
                        value={localMetadata.validation?.date?.minAge || ''}
                        onChange={(e) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              minAge: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Max Age (years)</Label>
                      <Input
                        type="number"
                        value={localMetadata.validation?.date?.maxAge || ''}
                        onChange={(e) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              maxAge: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Future dates only</Label>
                      <Switch
                        checked={localMetadata.validation?.date?.futureOnly || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              futureOnly: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Past dates only</Label>
                      <Switch
                        checked={localMetadata.validation?.date?.pastOnly || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              pastOnly: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Business days only</Label>
                      <Switch
                        checked={localMetadata.validation?.date?.businessDaysOnly || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            date: {
                              ...localMetadata.validation?.date,
                              businessDaysOnly: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Number Validation */}
            {isNumberType && (
              <AccordionItem value="number-validation">
                <AccordionTrigger>Number Validation</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Value</Label>
                      <Input
                        type="number"
                        value={localMetadata.validation?.number?.min || ''}
                        onChange={(e) =>
                          updateValidation({
                            number: {
                              ...localMetadata.validation?.number,
                              min: e.target.value ? parseFloat(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Max Value</Label>
                      <Input
                        type="number"
                        value={localMetadata.validation?.number?.max || ''}
                        onChange={(e) =>
                          updateValidation({
                            number: {
                              ...localMetadata.validation?.number,
                              max: e.target.value ? parseFloat(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Integer only</Label>
                      <Switch
                        checked={localMetadata.validation?.number?.integerOnly || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            number: {
                              ...localMetadata.validation?.number,
                              integerOnly: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Positive only</Label>
                      <Switch
                        checked={localMetadata.validation?.number?.positiveOnly || false}
                        onCheckedChange={(checked) =>
                          updateValidation({
                            number: {
                              ...localMetadata.validation?.number,
                              positiveOnly: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </TabsContent>

        {/* Formatting Tab */}
        <TabsContent value="formatting" className="space-y-4">
          <Accordion type="single" collapsible className="w-full">
            {/* Text Formatting */}
            {isTextType && (
              <AccordionItem value="text-formatting">
                <AccordionTrigger>Text Formatting</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div>
                    <Label>Capitalization</Label>
                    <Select
                      value={localMetadata.formatting?.text?.capitalization || 'none'}
                      onValueChange={(value) =>
                        updateFormatting({
                          text: {
                            ...localMetadata.formatting?.text,
                            capitalization: value as 'none' | 'first' | 'words' | 'all',
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="first">First Letter</SelectItem>
                        <SelectItem value="words">Words</SelectItem>
                        <SelectItem value="all">All Caps</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Prefix</Label>
                      <Input
                        placeholder="e.g., $"
                        value={localMetadata.formatting?.text?.prefix || ''}
                        onChange={(e) =>
                          updateFormatting({
                            text: {
                              ...localMetadata.formatting?.text,
                              prefix: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Suffix</Label>
                      <Input
                        placeholder="e.g., kg"
                        value={localMetadata.formatting?.text?.suffix || ''}
                        onChange={(e) =>
                          updateFormatting({
                            text: {
                              ...localMetadata.formatting?.text,
                              suffix: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Date Formatting */}
            {isDateType && (
              <AccordionItem value="date-formatting">
                <AccordionTrigger>Date Formatting</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div>
                    <Label>Date Format</Label>
                    <Input
                      placeholder="e.g., YYYY-MM-DD, MM/DD/YYYY"
                      value={localMetadata.formatting?.date?.format || ''}
                      onChange={(e) =>
                        updateFormatting({
                          date: {
                            ...localMetadata.formatting?.date,
                            format: e.target.value || undefined,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label>Locale</Label>
                    <Input
                      placeholder="e.g., en-US"
                      value={localMetadata.formatting?.date?.locale || ''}
                      onChange={(e) =>
                        updateFormatting({
                          date: {
                            ...localMetadata.formatting?.date,
                            locale: e.target.value || undefined,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Show time</Label>
                      <Switch
                        checked={localMetadata.formatting?.date?.showTime || false}
                        onCheckedChange={(checked) =>
                          updateFormatting({
                            date: {
                              ...localMetadata.formatting?.date,
                              showTime: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Show relative dates</Label>
                      <Switch
                        checked={localMetadata.formatting?.date?.relative || false}
                        onCheckedChange={(checked) =>
                          updateFormatting({
                            date: {
                              ...localMetadata.formatting?.date,
                              relative: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Description Formatting */}
            {isDescriptionType && (
              <AccordionItem value="description-formatting">
                <AccordionTrigger>Description Formatting</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div>
                    <Label>Format</Label>
                    <Select
                      value={localMetadata.formatting?.description?.format || 'plain'}
                      onValueChange={(value) =>
                        updateFormatting({
                          description: {
                            ...localMetadata.formatting?.description,
                            format: value as 'plain' | 'markdown' | 'html' | 'rich',
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="plain">Plain Text</SelectItem>
                        <SelectItem value="markdown">Markdown</SelectItem>
                        <SelectItem value="html">HTML</SelectItem>
                        <SelectItem value="rich">Rich Text</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Max Lines (preview)</Label>
                      <Input
                        type="number"
                        value={localMetadata.formatting?.description?.maxLines || ''}
                        onChange={(e) =>
                          updateFormatting({
                            description: {
                              ...localMetadata.formatting?.description,
                              maxLines: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Truncate Length</Label>
                      <Input
                        type="number"
                        value={localMetadata.formatting?.description?.truncateLength || ''}
                        onChange={(e) =>
                          updateFormatting({
                            description: {
                              ...localMetadata.formatting?.description,
                              truncateLength: e.target.value ? parseInt(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Truncate long text</Label>
                      <Switch
                        checked={localMetadata.formatting?.description?.truncate || false}
                        onCheckedChange={(checked) =>
                          updateFormatting({
                            description: {
                              ...localMetadata.formatting?.description,
                              truncate: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Show "Read more"</Label>
                      <Switch
                        checked={localMetadata.formatting?.description?.showReadMore || false}
                        onCheckedChange={(checked) =>
                          updateFormatting({
                            description: {
                              ...localMetadata.formatting?.description,
                              showReadMore: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </TabsContent>

        {/* Display Tab */}
        <TabsContent value="display" className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Show in list view</Label>
              <Switch
                checked={localMetadata.display?.showInList !== false}
                onCheckedChange={(checked) =>
                  updateDisplay({ showInList: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show in detail view</Label>
              <Switch
                checked={localMetadata.display?.showInDetail !== false}
                onCheckedChange={(checked) =>
                  updateDisplay({ showInDetail: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Searchable</Label>
              <Switch
                checked={localMetadata.display?.searchable || false}
                onCheckedChange={(checked) =>
                  updateDisplay({ searchable: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Filterable</Label>
              <Switch
                checked={localMetadata.display?.filterable || false}
                onCheckedChange={(checked) =>
                  updateDisplay({ filterable: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Sortable</Label>
              <Switch
                checked={localMetadata.display?.sortable || false}
                onCheckedChange={(checked) =>
                  updateDisplay({ sortable: checked })
                }
              />
            </div>
            <div>
              <Label>Help Text</Label>
              <Textarea
                placeholder="Help text shown below the field"
                value={localMetadata.display?.helpText || ''}
                onChange={(e) =>
                  updateDisplay({ helpText: e.target.value || undefined })
                }
                rows={2}
              />
            </div>
          </div>
        </TabsContent>

        {/* UI Tab */}
        <TabsContent value="ui" className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label>Placeholder</Label>
              <Input
                placeholder="e.g., Enter name..."
                value={localMetadata.ui?.placeholder || ''}
                onChange={(e) =>
                  updateUI({ placeholder: e.target.value || undefined })
                }
              />
            </div>
            <div>
              <Label>Hint</Label>
              <Textarea
                placeholder="Helper text shown below input"
                value={localMetadata.ui?.hint || ''}
                onChange={(e) =>
                  updateUI({ hint: e.target.value || undefined })
                }
                rows={2}
              />
            </div>
            <div>
              <Label>Icon</Label>
              <Input
                placeholder="Icon name or URL"
                value={localMetadata.ui?.icon || ''}
                onChange={(e) =>
                  updateUI({ icon: e.target.value || undefined })
                }
              />
            </div>
            <div>
              <Label>Size</Label>
              <Select
                value={localMetadata.ui?.size || 'md'}
                onValueChange={(value) =>
                  updateUI({ size: value as 'sm' | 'md' | 'lg' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

